import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Printer, Loader2, AlertTriangle, Flag, QrCode, Calendar, Quote, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShareMenu } from "@/components/ShareMenu";
import { exportReferences } from "@/lib/exportRefs";
import { downloadDocx, downloadPdf } from "@/lib/documentExport";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatReference, formatReferencePlain, formatReferenceHtml, orderReferences, isNumbered, yearSuffixMap, inTextCitation, numberedInText } from "@/lib/citations";
import { CitationText } from "@/components/CitationText";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { referencePageUrl } from "@/lib/publicUrl";
import api, { errMessage } from "@/lib/api";
import { copyText, copyRich } from "@/lib/clipboard";
import { BRAND } from "@/components/Brand";

const CANONICAL_BASE = "https://getciteqr.com";

// Counts one visit per page per browser tab session; strips the QR marker so reloads/shares aren't counted as QR.
function recordVisit(publicId) {
  const params = new URLSearchParams(window.location.search);
  const fromQr = params.get("src") === "qr";
  if (fromQr) {
    params.delete("src");
    const qs = params.toString();
    window.history.replaceState(window.history.state, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
  }
  const key = `cq-visit-${publicId}`;
  try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, "1"); } catch { /* storage unavailable */ }
  api.post(`/public/reference/${publicId}/visit${fromQr ? "?src=qr" : ""}`).catch(() => {});
}

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Per-reference Open Graph / Twitter tags. Canonical + og:url stay on getciteqr.com.
function applyOgTags(data, publicId) {
  const url = `${CANONICAL_BASE}/r/${publicId}`;
  const image = `${CANONICAL_BASE}/api/og-image/${publicId}.png`;
  const n = (data.references || []).length;
  const style = data.citation_style || "APA";
  const desc = [`${n} reference${n !== 1 ? "s" : ""}`, style, data.author_name, data.institution]
    .filter(Boolean)
    .join(" · ");
  const ogTitle = `${data.title} — References | ${BRAND.name}`;
  upsertMeta("property", "og:type", "article");
  upsertMeta("property", "og:site_name", BRAND.name);
  upsertMeta("property", "og:title", ogTitle);
  upsertMeta("property", "og:description", desc);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", image);
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", ogTitle);
  upsertMeta("name", "twitter:description", desc);
  upsertMeta("name", "twitter:image", image);
  upsertMeta("name", "description", desc);
  upsertCanonical(url);
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

const MONTHS_MLA = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Build a ready-made citation for THIS CiteQR reference page, in its own style.
function citePage(data, url, brand) {
  const style = data.citation_style || "APA";
  const author = (data.author_name || "").trim();
  const title = (data.title || "").trim();
  const d = data.updated_at ? new Date(data.updated_at) : new Date();
  const year = isNaN(d.getTime()) ? "" : String(d.getFullYear());
  const now = new Date();
  const mlaToday = `${now.getDate()} ${MONTHS_MLA[now.getMonth()]} ${now.getFullYear()}`;
  const fullToday = `${MONTHS_FULL[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
  const lead = author ? `${author.replace(/\.$/, "")}. ` : "";
  if (style === "MLA") {
    return `${lead}"${title}." ${brand}, ${year ? year + ", " : ""}${url}. Accessed ${mlaToday}.`;
  }
  if (style === "Chicago") {
    return `${lead}"${title}." ${brand}. ${year ? "Last modified " + fmtDate(data.updated_at) + ". " : ""}${url}.`;
  }
  if (style === "Vancouver" || style === "IEEE") {
    return `${lead}${title} [Internet]. ${brand}; ${year} [cited ${fullToday}]. Available from: ${url}`;
  }
  // APA
  return `${lead}(${year}). ${title} [Reference list]. ${brand}. ${url}`;
}

function CiteButton({ refItem, suffix, index, style }) {
  const numbered = isNumbered(style);
  const it = numbered ? null : inTextCitation(refItem, { suffix });
  const num = numberedInText(index + 1, style);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 shrink-0 gap-1 px-2 text-xs text-slate-400 hover:text-[#0D9488] print:hidden" data-testid={`cite-ref-btn-${index}`}>
          <Quote className="h-3.5 w-3.5" /> Cite
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <p className="px-1 pb-1 text-xs font-medium text-slate-400">In-text citation ({style})</p>
        {numbered ? (
          <button
            type="button"
            onClick={() => copyText(num, "Numbered citation copied.")}
            className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
            data-testid={`copy-numbered-btn-${index}`}
          >
            <span className="text-xs font-medium text-slate-500">Numbered</span>
            <span className="truncate text-slate-800">{num}</span>
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => copyText(it.parenthetical, "Parenthetical citation copied.")}
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
              data-testid={`copy-parenthetical-btn-${index}`}
            >
              <span className="text-xs font-medium text-slate-500">Parenthetical</span>
              <span className="truncate text-slate-800">{it.parenthetical}</span>
            </button>
            <button
              type="button"
              onClick={() => copyText(it.narrative, "Narrative citation copied.")}
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
              data-testid={`copy-narrative-btn-${index}`}
            >
              <span className="text-xs font-medium text-slate-500">Narrative</span>
              <span className="truncate text-slate-800">{it.narrative}</span>
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function PublicReferencePage() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [reason, setReason] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [notFoundMsg, setNotFoundMsg] = useState("");

  useEffect(() => {
    api.get(`/public/reference/${publicId}`)
      .then(({ data }) => {
        setData(data);
        setStatus("ok");
        document.title = `${data.title} — References | ${BRAND.name}`;
        applyOgTags(data, publicId);
        recordVisit(publicId);
      })
      .catch((e) => {
        setNotFoundMsg(e?.response?.data?.detail || "");
        setStatus("notfound");
      });
  }, [publicId]);

  const copyAll = () => {
    if (!data) return;
    const ordered = orderReferences(data.references, data.citation_style);
    const suffixes = yearSuffixMap(data.references);
    const numbered = isNumbered(data.citation_style);
    const marker = (i) => (data.citation_style === "IEEE" ? `[${i + 1}] ` : `${i + 1}. `);

    // Plain-text fallback (unchanged content & order).
    const plainRefs = ordered
      .map((r, i) => {
        const line = formatReferencePlain(r, data.citation_style, suffixes.get(r));
        return numbered ? `${marker(i).trim()} ${line}` : line;
      })
      .join("\n\n");
    const plain = `${data.title}\n\n${plainRefs}`;

    // Rich HTML: separate paragraphs, 0.5in hanging indent, double spacing, italics & hyperlinks.
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const refParas = ordered
      .map((r, i) => {
        const inner = formatReferenceHtml(r, data.citation_style, suffixes.get(r));
        const body = numbered ? `${esc(marker(i))}${inner}` : inner;
        return `<p style="margin:0 0 0 0; line-height:2; text-indent:-0.5in; margin-left:0.5in;">${body}</p>`;
      })
      .join("");
    const html =
      `<div style="font-family:'Times New Roman',Times,serif; font-size:12pt;">` +
      `<p style="margin:0 0 12pt 0; font-weight:bold; line-height:2;">${esc(data.title)}</p>` +
      refParas +
      `</div>`;

    copyRich(html, plain, "References copied to clipboard.");
  };

  const citeThisPage = () => {
    if (!data) return;
    const url = referencePageUrl(publicId);
    copyText(citePage(data, url, BRAND.name), `Citation for this page copied (${data.citation_style || "APA"}).`);
  };

  const downloadRefs = (format) => {
    if (!data?.references?.length) { toast.error("No references to export."); return; }
    const base = (data.title || "references").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "references";
    const n = data.references.length;
    const done = (label) => toast.success(`Downloaded ${n} reference${n > 1 ? "s" : ""} as ${label}.`);
    if (format === "docx") {
      downloadDocx(data.references, data.citation_style, data.title, base)
        .then(() => done("Word (.docx)"))
        .catch(() => toast.error("Couldn't generate the Word document."));
      return;
    }
    if (format === "pdf") {
      try {
        downloadPdf(data.references, data.citation_style, data.title, base);
        done("PDF");
      } catch (e) {
        toast.error("Couldn't generate the PDF.");
      }
      return;
    }
    exportReferences(data.references, data.citation_style, format, false, base);
    done(format.toUpperCase());
  };

  const submitReport = async () => {
    if (!reason.trim()) { toast.error("Please describe the issue."); return; }
    try {
      await api.post(`/public/report/${publicId}`, { reason, reporter_email: reporterEmail || null });
      toast.success("Report submitted. Thank you.");
      setReportOpen(false);
      setReason("");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>;
  }

  if (status === "notfound") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 text-center">
        <AlertTriangle className="h-10 w-10 text-slate-400" />
        <h1 className="mt-4 text-2xl font-bold text-slate-900">Reference page unavailable</h1>
        <p className="mt-2 max-w-md text-sm text-slate-600">{notFoundMsg || "This page may have been unpublished, deleted, or the link is incorrect."}</p>
        <Button asChild className="mt-6 bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/">Go to {BRAND.name}</Link></Button>
      </div>
    );
  }

  const suffixes = yearSuffixMap(data.references);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:py-16">
        <header className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium text-[#0D9488]">
            <QrCode className="h-4 w-4" /> Academic reference page
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900" data-testid="public-title">{data.title}</h1>
          {(data.author_name || data.institution) && (
            <p className="mt-2 text-base text-slate-600" data-testid="public-author">
              {data.author_name}{data.author_name && data.institution ? " · " : ""}{data.institution}
            </p>
          )}
          <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
            <Calendar className="h-3.5 w-3.5" />
            Updated {fmtDate(data.updated_at)}
          </div>
        </header>

        <div className="mt-6 flex flex-wrap gap-3 print:hidden">
          <Button variant="outline" onClick={copyAll} data-testid="copy-refs-btn"><Copy className="mr-2 h-4 w-4" /> Copy references</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="download-refs-btn"><Download className="mr-2 h-4 w-4" /> Download references</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel className="text-xs font-normal text-slate-500">Formatted documents</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => downloadRefs("docx")} data-testid="download-docx">Word (.docx) — formatted references</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => downloadRefs("pdf")} data-testid="download-pdf">PDF (.pdf) — formatted references</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-slate-500">Reference managers</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => downloadRefs("bibtex")} data-testid="download-bibtex">BibTeX (.bib) — Zotero, JabRef</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => downloadRefs("ris")} data-testid="download-ris">RIS (.ris) — EndNote, Mendeley</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-slate-500">Data</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => downloadRefs("csv")} data-testid="download-csv">CSV (.csv) — Spreadsheet</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ShareMenu url={referencePageUrl(publicId)} title={data.title} prefix="public-share" />
          <Button variant="outline" onClick={citeThisPage} data-testid="cite-page-btn"><Quote className="mr-2 h-4 w-4" /> Cite this page</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-refs-btn"><Printer className="mr-2 h-4 w-4" /> Print / Save PDF</Button>
          <Dialog open={reportOpen} onOpenChange={setReportOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" className="text-slate-500" data-testid="report-btn"><Flag className="mr-2 h-4 w-4" /> Report</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Report this page</DialogTitle>
                <DialogDescription>Let us know if this page contains inappropriate, harmful, or abusive content.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="report-reason">What's wrong?</Label>
                  <Textarea id="report-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="report-email">Your email <span className="text-slate-400">(optional)</span></Label>
                  <Input id="report-email" type="email" value={reporterEmail} onChange={(e) => setReporterEmail(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submitReport} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="report-submit">Submit report</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">References</h2>
          <Separator className="my-4" />
          {data.references.length === 0 ? (
            <p className="text-sm text-slate-500">No references have been added yet.</p>
          ) : isNumbered(data.citation_style) ? (
            <ol className="space-y-4" data-testid="public-references">
              {orderReferences(data.references, data.citation_style).map((r, i) => {
                const f = formatReference(r, data.citation_style, suffixes.get(r));
                return (
                  <li key={r.id || i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                    <span className="shrink-0 font-medium text-slate-500">
                      {data.citation_style === "IEEE" ? `[${i + 1}]` : `${i + 1}.`}
                    </span>
                    <span className="flex-1"><CitationText {...f} /></span>
                    <CiteButton refItem={r} suffix={suffixes.get(r)} index={i} style={data.citation_style} />
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="space-y-4" data-testid="public-references">
              {orderReferences(data.references, data.citation_style).map((r, i) => {
                const f = formatReference(r, data.citation_style, suffixes.get(r));
                return (
                  <div key={r.id || i} className="flex items-start gap-2">
                    <p
                      className="flex-1 text-sm leading-relaxed text-slate-700"
                      style={{ paddingLeft: "1.6em", textIndent: "-1.6em" }}
                    >
                      <CitationText {...f} />
                    </p>
                    <CiteButton refItem={r} suffix={suffixes.get(r)} index={i} style={data.citation_style} />
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <footer className="mt-8 text-center">
          <Link to="/" className="text-xs text-slate-400 hover:text-[#0D9488]" data-testid="citeqr-footer">Created with {BRAND.name}</Link>
        </footer>
      </div>
    </div>
  );
}
